import React, { createElement, Fragment } from "react";

import { AutocompleteState, AutocompleteApi } from "@algolia/autocomplete-core";
import { InternalAutocompleteSource } from "@algolia/autocomplete-core";
import { parseAlgoliaHitHighlight } from "@algolia/autocomplete-preset-algolia";
import { css } from "@compiled/react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import SearchIcon from "@atlaskit/icon/core/search";
import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import wacToken from "@/libraries/design/foundations/token/src/wac-token";
import { experiments } from "@/utils/experiment/manifest";
import { useExperiment } from "@/utils/experiment/use-experiment";

import { handleAlgoliaConversionMetrics } from "./auto-complete-helpers";
import { ContentHit } from "./auto-complete-search-bar";

type AutoCompleteResultsDropdownProps = {
    isMobileView: boolean;
    panelRef: React.Ref<HTMLDivElement>;
    autocompleteState: AutocompleteState<ContentHit>;
    autocomplete: AutocompleteApi<
        ContentHit,
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        React.BaseSyntheticEvent<object, any, any>,
        React.MouseEvent<Element, MouseEvent>,
        React.KeyboardEvent<Element>
    >;
    aiEnabled: boolean;
};

const aaPanel = css({
    backgroundColor: wacToken("color.neutral.light0"),
    boxShadow: `0 0 0 1px ${wacToken(
        "color.neutral.light300"
    )}, 0 6px 16px -4px ${wacToken("color.neutral.light300")}`,
    margin: "8px 0 0",
    overflow: "hidden",
    position: "absolute",
    transition: "opacity .2s ease-in, filter .2s ease-in",
    borderRadius: "15px 15px 15px 15px",
    padding: "24px",
    width: "480px",

    [media.below.sm]: {
        padding: "8px",
        width: "100%"
    }
});

const aaPanelAI = css({
    padding: "24px 16px"
});

const aaPanelStalled = css({
    filter: "grayscale(1)",
    opacity: ".8"
});

const aaItem = css({
    "alignItems": "center",
    "borderRadius": "3px",
    "cursor": "pointer",
    "display": "grid",
    "minHeight": 40,
    "padding": "4px",
    "marginBlockStart": "8px",
    "marginBlockEnd": "8px",

    "&:first-child": {
        marginBlockStart: "0px"
    },

    "&[aria-selected=true]": {
        visibility: "visible",
        backgroundColor: wacToken("color.neutral.light200")
    }
});

const aaItemAI = css({
    padding: "4px 8px"
});

const aaItemContent = css({
    "alignItems": "center",
    "cursor": "pointer",
    "display": "grid",
    "gap": 8,
    "gridGap": 8,
    "gridAutoFlow": "column",
    "lineHeight": "1.25em",
    "overflow": "hidden",

    ":empty": {
        display: "none"
    }
});

const aaItemContentAI = css({
    gap: 10,
    gridGap: 10
});

const aaItemContentBodyAI = css({
    "display": "grid",
    "gridTemplateColumns": "auto 1fr",
    "gap": 10,
    "gridGap": 10,

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& svg": {
        marginBlockStart: 2
    }
});

const aaItemContentTitle = css({
    display: "inline-block",
    margin: "0 .5em 0 0",
    maxWidth: "100%",
    overflow: "hidden",
    padding: "0",
    textOverflow: "ellipsis",
    whiteSpace: "normal"
});

const aaItemContentTitleAI = css({
    margin: "0",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0
});

const aaItemWrapper = css({
    alignItems: "center",
    color: "inherit",
    display: "grid",
    gap: 4,
    gridGap: 4,
    gridAutoFlow: "column",
    justifyContent: "space-between",
    width: "100%"
});

const aaItemLink = css({
    alignItems: "center",
    color: "inherit",
    display: "grid",
    gap: 4,
    gridGap: 4,
    gridAutoFlow: "column",
    justifyContent: "space-between",
    width: "100%",
    textDecoration: "none"
});

const decodeHtml = (html: string): string => {
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
};

export const SearchTitleResult = ({ hit }: { hit: ContentHit }) => {
    return createElement(
        Fragment,
        {},
        parseAlgoliaHitHighlight({ hit, attribute: "title" }).map(
            ({ value, isHighlighted }, index) => {
                const decodedValue = decodeHtml(value);

                if (isHighlighted) {
                    return createElement(
                        "mark",
                        {
                            key: index,
                            style: {
                                color: wacToken("color.neutral.dark200"),
                                fontStyle: "normal",
                                fontWeight: "600",
                                backgroundColor: "transparent"
                            }
                        },
                        decodedValue
                    );
                }

                return decodedValue;
            }
        )
    );
};

export const AutoCompleteResultsDropdown = ({
    isMobileView = false,
    panelRef,
    autocompleteState,
    autocomplete,
    children,
    aiEnabled
}: React.PropsWithChildren<AutoCompleteResultsDropdownProps>) => {
    if (!isMobileView) {
        return (
            <div
                ref={panelRef}
                css={[
                    aaPanel,
                    aiEnabled && aaPanelAI,
                    autocompleteState.status === "stalled" && aaPanelStalled
                ]}
                className={[
                    "aa-Panel",
                    "aa-Panel--desktop",
                    autocompleteState.status === "stalled" &&
                        "aa-Panel--stalled"
                ]
                    .filter(Boolean)
                    .join(" ")}
                {...autocomplete.getPanelProps({})}
            >
                {children}
            </div>
        );
    } else {
        return <>{children}</>;
    }
};

interface AutocompleteResultProps {
    item: ContentHit;
    source: InternalAutocompleteSource<ContentHit>;
    autocomplete: AutocompleteApi<
        ContentHit,
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        React.BaseSyntheticEvent<object, any, any>,
        React.MouseEvent<Element, MouseEvent>,
        React.KeyboardEvent<Element>
    >;

    isMobileView?: boolean;
}

export const DefaultAutocompleteResult = ({
    item,
    source,
    autocomplete,
    isMobileView = false
}: AutocompleteResultProps) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const wacAISearchSummaryExperiment = useExperiment(
        experiments.wacAISearchSummaryV2
    );
    const isAISearchSummaryEnabled =
        wacAISearchSummaryExperiment.type === "enrolled" &&
        wacAISearchSummaryExperiment.value;

    const handleSearchResultClick = (item: ContentHit) => {
        handleAlgoliaConversionMetrics(item);
        createAnalyticsEvent({
            eventName: "clickAutocompleteSearchResult",
            eventType: "ui",
            action: "clicked",
            actionSubject: "linkButton",
            actionSubjectId: "globalNavSearchResults",
            source: "globalNav",
            attributes: {
                product: "search",
                queryId: item.queryID,
                objectId: item.objectID,
                linkUrl: item.url,
                pageUrl: window.location.href,
                pagePath: window.location.pathname,
                rank: item.position
            }
        }).fire();
    };

    return (
        <li
            key={item.objectID}
            className="aa-Item"
            css={[aaItem, isAISearchSummaryEnabled && aaItemAI]}
            {...autocomplete.getItemProps({ item, source })}
        >
            <a
                className="aa-ItemLink"
                aria-label={item.title}
                css={[aaItemLink]}
                href={item.url}
                onClick={(event) => {
                    event.preventDefault();
                    const relativeUrlPath =
                        (item?.url?.split(
                            "https://www.atlassian.com"
                        )[1] as string) || item?.url;
                    handleSearchResultClick(item);
                    window.location.href = relativeUrlPath;
                }}
            >
                <div className="aa-ItemWrapper" css={[aaItemWrapper]}>
                    <div
                        className="aa-ItemContent"
                        css={[
                            aaItemContent,
                            isAISearchSummaryEnabled && aaItemContentAI
                        ]}
                    >
                        <div
                            className="aa-ItemContentBody"
                            css={[
                                isAISearchSummaryEnabled && aaItemContentBodyAI
                            ]}
                        >
                            {isAISearchSummaryEnabled && !isMobileView && (
                                <SearchIcon
                                    label="Search result"
                                    size="medium"
                                    color={token("color.icon.accent.gray")}
                                />
                            )}
                            <div
                                css={[
                                    css({
                                        display: "flex",
                                        alignItems: isMobileView
                                            ? "flex-start"
                                            : "center",
                                        gap: "8px",
                                        minWidth: 0,
                                        flex: 1
                                    })
                                ]}
                            >
                                {isMobileView && (
                                    <SearchIcon
                                        label="Search result"
                                        size="small"
                                        color={token("color.icon")}
                                    />
                                )}
                                <div
                                    className="aa-ItemContentTitle"
                                    css={[
                                        aaItemContentTitle,
                                        isAISearchSummaryEnabled &&
                                            aaItemContentTitleAI
                                    ]}
                                >
                                    <SearchTitleResult hit={item} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </a>
        </li>
    );
};
